<script lang="ts" setup>
import { computed, ref, defineProps } from 'vue'
import { useStore } from 'vuex'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import RequestFieldEditModal from './RequestFieldEditModal.vue'
import {
  EditingAccess,
  RequestFieldType,
  RequestFieldValue
} from 'src/pages/request_show/store/request_state_declarations'
import { UserState } from 'src/store/globalModule'
import DOMPurify from 'dompurify'

interface RequestFieldValuesProps {
  fields: RequestFieldValue[]
}

const props = defineProps<RequestFieldValuesProps>()
const store = useStore()

/**
 * Determines if the current user can edit a given field.
 *
 * Note: Direct permission logic is used here because CASL didn't properly evaluate
 * field.request_field_version.editing_access conditions despite correct backend configuration.
 * This manual check follows the same permission rules:
 * - Admins: can edit any field
 * - Publishers/Department Admins: can edit fields with editing_access 1 or 2
 * - Requesters: can edit their own fields if editing_access is 2
 */
const canEditField = (field: RequestFieldValue) => {
  const currentUser: UserState | null = store.state.currentUser
  if (!currentUser) {
    return false
  }

  const role = currentUser.user_role
  if (role === 'admin') {
    return true
  }

  const editingAccess = field.request_field_version?.editing_access
  if (editingAccess === undefined) {
    return false
  }

  if (
    (role === 'publisher' || role === 'department_admin') &&
    (editingAccess === EditingAccess.PUBLISHER ||
      editingAccess === EditingAccess.REQUESTER)
  ) {
    return true
  }

  if (editingAccess === EditingAccess.REQUESTER) {
    const isRequester = store.state.request_show?.request?.is_current_requester
    if (isRequester) {
      return true
    }
  }

  return false
}

const showEditModal = ref(false)
const selectedField = ref<RequestFieldValue | null>(null)

const filteredRequestLoading = computed(
  () => store.state.request_show.filteredRequestLoading
)

const openEditModal = (field: RequestFieldValue) => {
  selectedField.value = field
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  selectedField.value = null
}

const formatValue = (field: RequestFieldValue) => {
  switch (field.field_type) {
    case RequestFieldType.Text:
      return field.value || ''
    case RequestFieldType.Number:
      return field.value || ''
    case RequestFieldType.Checkbox:
    case RequestFieldType.Dropdown:
      return Array.isArray(field.value)
        ? field.value.map((option) => option.name).join(', ')
        : ''
    case RequestFieldType.Radio:
      return field.value?.name || ''
    case RequestFieldType.RichText:
      return DOMPurify.sanitize(field.value) || ''
    case RequestFieldType.Datepicker:
      return field.value || ''
    default:
      return ''
  }
}
</script>

<template>
  <article class="panel custom-fields-panel p-bottom-4">
    <p
      class="panel-heading has-background-light has-text-weight-semibold is-size-4"
    >
      Additional information
    </p>
    <div class="panel-content" v-if="!filteredRequestLoading">
      <div
        v-for="field in props.fields"
        :key="field.id || `new-field-${field.field_id}`"
        class="panel-content-section"
      >
        <div
          class="panel-item is-flex is-align-items-center is-justify-content-space-between"
        >
          <p class="has-text-weight-semibold is-flex is-align-items-center">
            {{ field.display_name }}
            <NrTooltip
              label="This field has changed since the making of the request"
              v-if="
                field.id &&
                (field.field_settings_have_changed ||
                  field.field_value_was_edited)
              "
            >
              <span class="info-icon m-left-8">
                <NrIconButton
                  variant="minimal-neutral"
                  :icon="{ name: 'info-circle', type: 'regular' }"
                  :aria-label="'Field change information'"
                />
              </span>
            </NrTooltip>
          </p>
          <NrTooltip
            label="Edit field"
            class="panel-item-edit"
            v-if="canEditField(field)"
          >
            <NrIconButton
              variant="minimal-neutral"
              :icon="{ name: 'pencil-alt', type: 'regular' }"
              :aria-label="`Edit ${field.display_name}`"
              @click="openEditModal(field)"
            />
          </NrTooltip>
        </div>
        <div class="panel-item">
          <p class="m-left-8 m-bottom-12">
            <span
              v-if="
                field.value && field.field_type !== RequestFieldType.RichText
              "
            >
              {{ formatValue(field) }}
            </span>
            <span
              class="rich-text-value"
              v-else-if="
                field.value && field.field_type === RequestFieldType.RichText
              "
              v-html="formatValue(field)"
            ></span>
            <span v-else class="is-italic">No data entered</span>
          </p>
        </div>
      </div>
    </div>
    <NrLoadingAnimation v-else :count="3" />
    <RequestFieldEditModal
      v-if="selectedField && showEditModal"
      :field="selectedField"
      @close="closeEditModal"
    />
  </article>
</template>

<style scoped lang="scss">
.rich-text-value :deep(ul),
.rich-text-value :deep(ol) {
  list-style-position: inside;
}

.rich-text-value :deep(ul) {
  list-style-type: disc;
}

.rich-text-value :deep(ol) {
  list-style-type: decimal;
}

.rich-text-value :deep(ul ul),
.rich-text-value :deep(ol ul) {
  list-style-type: circle;
  margin-left: 15px;
}

.rich-text-value :deep(ol ol),
.rich-text-value :deep(ul ol) {
  list-style-type: lower-latin;
  margin-left: 15px;
}
</style>
